// ---------------------------------------------
// Logo Banner - DeSight Shopify Theme
// Animated Marquee functionality
// ---------------------------------------------

/**
 * Logo Banner
 * Handles marquee animation for logo carousel
 */
export class LogoBanner {
  static initializedSections = new Set();

  constructor(section) {
    this.section = section;
    this.track = this.section.querySelector('.logo-banner__track');
    this.inner = this.section.querySelector('.logo-banner__inner');
    this.cloned = false;

    if (!this.track || !this.inner) {
      return;
    }

    if (LogoBanner.initializedSections.has(this.section)) {
      return;
    }
    LogoBanner.initializedSections.add(this.section);

    this.init();
  }

  init() {
    this.updateCSSVariables();
    this.waitForImages();
    this.bindEvents();
    window.addEventListener('resize', () => this.debouncedResize());
  }

  debouncedResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      if (!this.cloned) {
        this.waitForImages();
      }
    }, 250);
  }

  waitForImages() {
    const images = this.inner.querySelectorAll('img');

    if (images.length === 0) {
      this.cloneLogos();
      return;
    }

    let loadedCount = 0;
    images.forEach(img => {
      if (img.complete) {
        loadedCount++;
      } else {
        img.addEventListener('load', () => {
          loadedCount++;
          if (loadedCount === images.length) {
            setTimeout(() => this.cloneLogos(), 50);
          }
        });
        img.addEventListener('error', () => {
          loadedCount++;
          if (loadedCount === images.length) {
            setTimeout(() => this.cloneLogos(), 50);
          }
        });
      }
    });

    if (loadedCount === images.length) {
      setTimeout(() => this.cloneLogos(), 50);
    }
  }

  updateCSSVariables() {
    const computedStyle = getComputedStyle(this.section);
    const sectionDuration = computedStyle.getPropertyValue('--marquee-duration').trim();
    const sectionGap = computedStyle.getPropertyValue('--logo-gap').trim() || '24px';

    if (sectionDuration) {
      this.inner.style.setProperty('--marquee-duration', sectionDuration);
    }
    this.inner.style.gap = sectionGap;
  }

  cloneLogos() {
    if (this.cloned) return;

    const slides = this.inner.querySelectorAll('.logo-banner__slide');
    if (slides.length === 0) return;

    const trackWidth = this.track.offsetWidth;
    if (trackWidth === 0) return;

    let singleSetWidth = 0;
    slides.forEach(slide => {
      singleSetWidth += slide.offsetWidth;
    });

    const computedStyle = getComputedStyle(this.inner);
    const gap = parseFloat(computedStyle.gap) || 24;
    singleSetWidth += gap * slides.length;

    if (singleSetWidth === 0) return;

    const clonesNeeded = Math.ceil(trackWidth / singleSetWidth) + 2;

    for (let i = 0; i < clonesNeeded; i++) {
      slides.forEach(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.inner.appendChild(clone);
      });
    }

    this.cloned = true;
  }

  bindEvents() {
    const pauseOnHover = this.section.classList.contains('logo-banner--pause-on-hover');

    if (pauseOnHover) {
      this.section.addEventListener('mouseenter', () => {
        this.inner.classList.add('logo-banner__inner--paused');
      });

      this.section.addEventListener('mouseleave', () => {
        this.inner.classList.remove('logo-banner__inner--paused');
      });
    }
  }

  static initAll() {
    const sections = document.querySelectorAll('.logo-banner');
    sections.forEach(section => {
      new LogoBanner(section);
    });
  }
}

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.logo-banner');
  if (section) {
    new LogoBanner(section);
  }
});
